import React, { useMemo } from 'react';
import {
  Box,
  Center,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Input,
  Link,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { ArcButton } from '../../common/components/ArcButton';
import { LanguageSelector } from '../../common/components/LanguageSelector';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { OutlineGradientIconAnimated, SEHomeNavHomeIcon } from '../../styles/custom-icons';
import { openPrivacyNoticePdfInWebView } from '../account/settings/privacy/privacy-notice-pdf';
import { openTermsOfUsePdfInWebView } from '../account/terms-of-use-pdf';
import { makePostcodeFormSchema } from '../site/site-helpers';
import { selectSite } from '../site/siteSlice';
import { PostcodeFormData, PostcodeInput } from './PostcodeInput';
import { selectSites } from './sitesSlice';

export type SiteFormData = {
  name: string;
  address: string;
  city: string;
  state: string;
  postCode: string;
  country: string;
  latitude: number;
  longitude: number;
  timezone: string;
};

type Props = {
  onSubmit: (values: SiteFormData) => void;
  readonly externalError?: string;
};

export function SiteForm({ onSubmit, externalError }: Props) {
  const site = useSelector(selectSite);
  const { sites } = useSelector(selectSites);
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const schema = yup.object().shape({
    ...makePostcodeFormSchema(t),
    name: yup.string().max(50).required().label(t('Common.home name')),
  });
  const {
    register,
    control,
    getValues,
    reset,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SiteFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      postCode: site.post_code ?? '',
      name: site.site_name.includes('cIAM') ? '' : site.site_name,
    },
  });
  const backgroundColor = useColorModeValue('#FFFFFF', 'customGrey.900');
  const iconColor = useColorModeValue('primaryBranding.600', 'primaryBranding.200');
  const linkColor = useColorModeValue('schneiderSkyBlue.600', 'schneiderSkyBlue.200');

  async function handlePrivacyNoticeClick() {
    await openPrivacyNoticePdfInWebView(i18n.language);
  }

  async function handleTermsOfUseClick() {
    await openTermsOfUsePdfInWebView(i18n.language);
  }
  const { isOpen: isLogoutOpen, onOpen: onLogoutOpen, onClose: onLogoutClose } = useDisclosure();

  const urlSearchParamsToGoBack = useMemo(() => {
    const params = new URLSearchParams(search);
    params.set('direction', 'back');
    return params.toString();
  }, [search]);

  const backURL = useMemo(() => {
    if (sites.length === 1) {
      // If a user has one site and a site id isn't in the search params, we know they are creating a new site --
      // -- for the first time. In this case, we want to provide the logout modal (instead of the sites list)
      if (urlSearchParamsToGoBack.includes(`${site.site_id}`)) {
        return undefined;
      }
    }
    return `/sites/list?${urlSearchParamsToGoBack}`;
  }, [sites.length, urlSearchParamsToGoBack, site.site_id]);

  const onClickBack = useMemo(() => {
    if (!backURL) return onLogoutOpen;
    return undefined;
  }, [backURL, onLogoutOpen]);

  function handleSelectLocation(location: PostcodeFormData) {
    const currentValues = getValues();
    reset({
      ...currentValues,
      ...location,
    });
  }

  return (
    <SlidingAnimationPageBase
      px={5}
      includeTopNav
      title={t('Common.get started')}
      backURL={backURL}
      onClickBack={onClickBack}
    >
      <Center flexDirection="column">
        <Center w="300px" h="300px">
          <OutlineGradientIconAnimated height="100%" width="100%" color={iconColor} />
          <Center pos="absolute">
            <SEHomeNavHomeIcon color={iconColor} w="100px" h="100px" marginBottom="15px" />
          </Center>
        </Center>
        <Text fontSize={32} fontWeight={400} textAlign="center">
          {t('Set Address.welcome')}
        </Text>
        <Text textAlign="center">
          {t('Set Address.turn sunshine into savings')} {t('Set Address.well spot bill boosters')}
        </Text>
      </Center>
      <Box onSubmit={handleFormSubmit(onSubmit)} as={'form'} data-testid="site-form" mt={2}>
        <PostcodeInput
          control={control}
          error={externalError || errors?.postCode?.message}
          handleSelectLocation={handleSelectLocation}
        />

        <FormControl mt={3} isInvalid={!!errors.name}>
          <Input
            {...register('name')}
            borderRadius={0}
            height="50px"
            type="text"
            data-private
            autoComplete="off"
            data-testid="site-home-name"
            background={backgroundColor}
            borderColor={'customGrey.400'}
            placeholder={`${t('Common.home name')}...`}
          />
          <FormHelperText mb={1}>{t('Common.home name example')}</FormHelperText>
          <FormErrorMessage data-testid="site-name-error">{errors?.name?.message}</FormErrorMessage>
        </FormControl>

        <LanguageSelector mt={4} />

        <Center flexDirection="column">
          <ArcButton
            isLoading={isSubmitting}
            my={5}
            type="submit"
            data-testid="save-button"
            w={'80%'}
            arcColor="#3DCD57"
          >
            {t('Common.continue')}
          </ArcButton>
          <Text mx={8} mb={6} textAlign="center">
            {t('Set Address.by clicking continue')}{' '}
            <Link
              fontWeight={600}
              data-testid="privacy-notice-link"
              color={linkColor}
              onClick={handlePrivacyNoticeClick}
            >
              {t('Privacy Notice.privacy notice')}
            </Link>{' '}
            {t('Set Address.and')}{' '}
            <Link fontWeight={600} data-testid="terms-of-use-link" color={linkColor} onClick={handleTermsOfUseClick}>
              {t('Common.terms of use')}
            </Link>
          </Text>
        </Center>
      </Box>
      <AlertDialogModal
        isOpen={isLogoutOpen}
        onClose={onLogoutClose}
        header={t('Account.logout')}
        subHeader={t('Account.are you sure logout')}
        confirmButtonName={t('Account.logout')}
        onConfirm={() => navigate('/logout')}
        cancelButtonName={t('Common.cancel')}
      />
    </SlidingAnimationPageBase>
  );
}
